/**
 * A pill: a filter chip, or a name you can pick.
 *
 * There were two hand-written copies of this, one 32px tall and one 36px, differing
 * only in the bits nobody meant to change. Both had the same fault: text that wrapped
 * inside a fixed-height pill, so "In progress" became two lines in a box tall enough
 * for one and the second line hung out the bottom.
 *
 * `whitespace-nowrap` and `shrink-0` are the whole fix, and having one component is
 * what stops the third copy arriving without them.
 */

import type { ReactNode } from 'react';

const BASE =
  'inline-flex items-center px-3 h-9 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap shrink-0';

export function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${BASE} ${
        active
          ? 'bg-primary text-[var(--color-on-primary)] border-primary'
          : 'bg-surface text-muted border-border hover:bg-surface-alt hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The row they sit in.
 *
 * `scroll` for a fixed set of filters, which should stay on one line and slide: five
 * job statuses do not fit a phone, and a pill half off the right edge reads as a
 * rendering fault rather than as "there is more this way". The negative margin lets
 * them slide to the true screen edge while the first one still lines up with the text
 * above it.
 *
 * `wrap` for an open-ended set — thirty engineers to pick from is a paragraph, not a
 * line, and a horizontal scroll containing thirty names hides most of them.
 */
export function PillRow({
  children,
  layout = 'scroll',
  className = '',
}: {
  children: ReactNode;
  layout?: 'scroll' | 'wrap';
  className?: string;
}) {
  return layout === 'wrap' ? (
    <div className={`flex flex-wrap gap-2 ${className}`}>{children}</div>
  ) : (
    <div
      className={`flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
    >
      {children}
    </div>
  );
}
